<template>
  <div
    class="eloca-radio-group"
    :class="{ 'eloca-radio-group--inline': inline }"
    role="radiogroup"
    :aria-label="ariaLabel || null"
    :aria-labelledby="ariaLabelledby || null"
  >
    <label
      v-for="opt in options"
      :key="optionKey(opt)"
      class="eloca-radio-group__item"
      :class="{ 'is-disabled': disabled || opt.disabled }"
    >
      <input
        type="radio"
        class="eloca-radio-group__input"
        :name="name"
        :value="opt.value"
        :checked="isChecked(opt.value)"
        :disabled="disabled || opt.disabled"
        @change="onChange(opt.value)"
      />
      <span class="eloca-radio-group__control" aria-hidden="true" />
      <span class="eloca-radio-group__label">{{ opt.label }}</span>
    </label>
  </div>
</template>

<script>
/**
 * Grupo de rádios nativos com aparência consistente (tema claro/escuro do painel).
 * Evita v-radio do Vuetify, que conflita com o CSS isolado e o tema escuro.
 */
export default {
  name: 'RadioGroup',
  props: {
    /** Valor selecionado (v-model) */
    value: {
      type: [String, Number, Boolean],
      default: null,
    },
    /** [{ value, label, disabled? }, ...] */
    options: {
      type: Array,
      required: true,
    },
    /** Atributo name dos inputs (obrigatório para agrupamento) */
    name: {
      type: String,
      required: true,
    },
    /** Itens em linha (flex-wrap), como o antigo row do Vuetify */
    inline: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Acessibilidade: nome do grupo quando não há label visível acima */
    ariaLabel: {
      type: String,
      default: '',
    },
    /** id do elemento que rotula o grupo (ex.: label acima do grupo) */
    ariaLabelledby: {
      type: String,
      default: '',
    },
  },
  methods: {
    optionKey(opt) {
      return String(opt.value);
    },
    isChecked(v) {
      return this.value === v;
    },
    onChange(v) {
      this.$emit('input', v);
      this.$emit('change', v);
    },
  },
};
</script>

<style scoped>
.eloca-radio-group {
  --eloca-radio-ring: rgba(0, 123, 255, 0.35);
  --eloca-radio-border: #adb5bd;
  --eloca-radio-border-hover: #6c757d;
  --eloca-radio-checked: #007bff;
  --eloca-radio-dot: #fff;
  --eloca-radio-label: #212529;
}

.eloca-radio-group__item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 0.5rem;
  line-height: 1.35;
  font-size: 0.875rem;
}

.eloca-radio-group__item:last-child {
  margin-bottom: 0;
}

.eloca-radio-group__item.is-disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.eloca-radio-group__input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  margin: 0;
  /* mantém foco por teclado; o clique usa o <label> */
}

.eloca-radio-group__control {
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 2px solid var(--eloca-radio-border);
  border-radius: 50%;
  background: transparent;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.eloca-radio-group__item:hover:not(.is-disabled) .eloca-radio-group__control {
  border-color: var(--eloca-radio-border-hover);
}

.eloca-radio-group__input:focus + .eloca-radio-group__control {
  box-shadow: 0 0 0 3px var(--eloca-radio-ring);
}

.eloca-radio-group__input:checked + .eloca-radio-group__control {
  border-color: var(--eloca-radio-checked);
  background-color: var(--eloca-radio-checked);
}

.eloca-radio-group__input:checked + .eloca-radio-group__control::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  margin: -4px 0 0 -4px;
  border-radius: 50%;
  background-color: var(--eloca-radio-dot);
}

.eloca-radio-group__input:checked:focus + .eloca-radio-group__control {
  box-shadow: 0 0 0 3px var(--eloca-radio-ring);
}

.eloca-radio-group__label {
  color: var(--eloca-radio-label);
  flex: 1 1 auto;
  min-width: 0;
}

/* Layout em linha */
.eloca-radio-group--inline {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.5rem 1.25rem;
}

.eloca-radio-group--inline .eloca-radio-group__item {
  margin-bottom: 0;
}
</style>
